import React, { useEffect, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { getFirstCallRecordKey } from 'flow/utils/call-records-utils';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import {
    makeSelectOwnCallRecordsForContact,
    selectOwnCallRecordsNextBeforeForNumber,
} from 'flow/selectors/call-records-selectors';
import { usePrevious } from 'flow/hooks';
import { getContact } from 'flow/selectors/contact-selectors';
import ContactBox from 'flow/components/contact-card/ContactBoxTW';
import { getCallableNumberFromContact } from 'flow/utils/extension-utils';

import { fetchAllCallRecordsForContact } from 'flow/actions/call-records-actions';
import { routes } from 'flow/types/routes';
import { generatePath, useHistory } from 'react-router-dom';
import { ContactKey } from 'flow/types/EntityKeys';
import { Contact } from 'flow/types/Contact';
import { TypedMap } from 'flow/types/TypedMap';

import ContactCallRecordItems from './ContactCallRecordItems';

interface Props {
    contactKey: ContactKey;
    contact: TypedMap<Contact>;
    onNavigation?: () => void;
}

export const ContactCallHistoryWidgetTW = ({
    contactKey,
    contact,
    onNavigation,
}: Props) => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const contactToUse = useAppSelector(
        (state) => contact || getContact(state, contactKey),
    );
    const otherPartNumber = getCallableNumberFromContact(contactToUse);
    const prevOtherPartNumber = usePrevious(otherPartNumber);

    const selectOwnCallRecordsForContact = useMemo(
        makeSelectOwnCallRecordsForContact,
        [],
    );
    const callRecords = useAppSelector((state) =>
        selectOwnCallRecordsForContact(state, contactToUse),
    );

    const callRecordsNextBeforeTimestamp = useAppSelector((state) =>
        selectOwnCallRecordsNextBeforeForNumber(state, otherPartNumber),
    );

    useEffect(() => {
        if (prevOtherPartNumber !== otherPartNumber)
            dispatch(
                fetchAllCallRecordsForContact(
                    ownExtensionKey,
                    callRecordsNextBeforeTimestamp,
                    contactToUse,
                ),
            );
    }, [
        callRecordsNextBeforeTimestamp,
        prevOtherPartNumber,
        ownExtensionKey,
        otherPartNumber,
        contactToUse,
    ]);

    if (!callRecords || callRecords.isEmpty()) {
        return null;
    }

    const goToCallHistory = () => {
        onNavigation?.();

        history.push(
            generatePath(routes['calls.callRecordKey.list'], {
                callRecordKey: getFirstCallRecordKey(callRecords),
            }),
            { from: location.pathname },
        );
    };

    return (
        <ContactBox
            headerTitle={i18next.t('call.history')}
            handleNavigate={goToCallHistory}
        >
            <ContactCallRecordItems callRecords={callRecords} />
        </ContactBox>
    );
};

export default ContactCallHistoryWidgetTW;
